/*
 * Domain model of the storefront. Pages and components only ever see these types.
 * Phase 1 fills them from mock data (services/mock). Phase 4 maps the real API responses
 * into the same types (services/http), so no page changes when the backend is wired.
 *
 * Conventions: dates are ISO `yyyy-MM-dd`, times `HH:mm`, money is a number in MAD
 * (the platform currency). Display conversion happens in lib/format.
 */

export type Universe = 'veh' | 'exp';
export type VehicleKind = 'car' | 'moto';
export type Currency = 'MAD' | 'EUR' | 'USD';
/** Any stored or unknown value as a currency, MAD by default (the old site's `coerceCurrency`, L5). */
export const coerceCurrency = (value: unknown): Currency =>
  value === 'EUR' || value === 'USD' || value === 'MAD' ? value : 'MAD';
export type Language = 'fr' | 'en' | 'ar';
/** Platform setting (GET /api/config/platform), never a client choice. */
export type PaymentMode = 'full' | 'commission_only';
export type Tone = 'lime' | 'mint' | 'ink' | 'white' | 'warn' | 'saffron' | 'sand';

/**
 * The «all categories» value of a category filter (search state, URL). A value, not a label: the UI shows
 * `common:allCategories` («Toutes» / «All»).
 */
export const ALL_CATEGORIES = 'Toutes'; // i18n-ignore: filter value kept from phase 1 (state and URLs), not a label

export interface PlatformConfig {
  paymentMode: PaymentMode;
  activitiesEnabled: boolean;
  /** Long-stay discount, platform-wide (from 7 and 28 billable days: fixed server-side). */
  longStay: { weeklyPct: number; monthlyPct: number };
  /**
   * Display rates from MAD (`GET /api/currency/rates`, the rate the card is charged at). Null when the rate is
   * missing or invalid: prices then show and are charged in MAD (API-FX-1), never at an indicative rate.
   */
  rates: Record<Currency, number | null>;
  /** Platform-wide minimum rental length (a floor over each vehicle's own minimum). */
  minRentalDays?: number;
}

export interface City {
  id: string;
  name: string;
  /** Short line under the name, e.g. «24 véhicules» or «Aéroport RAK». */
  caption: string;
}

/** A city tile on the home page. */
export interface Destination {
  city: string;
  vehicles: number;
  /** Airport code shown on the tile. */
  code: string;
  image?: string;
  /** `srcset` of `image` (640w / 1280w). */
  imageSrcSet?: string;
  imageAlt?: string;
}

/** A category with its cheapest price and a representative vehicle image. */
export interface CategorySummary {
  category: string;
  count: number;
  fromPrice: number;
  image: string;
}

/* ── Vehicles ── */

export interface VehicleSearch {
  city: string;
  kind: VehicleKind;
  start: string;
  end: string;
  pickupTime: string;
  returnTime: string;
  delivery: boolean;
}

export interface VehicleFilters {
  category: string;
  transmission: 'all' | 'Manuelle' | 'Automatique';
  freeDelivery: boolean;
  sort: 'price-asc' | 'price-desc' | 'deposit-asc';
}

export interface VehicleTag {
  label: string;
  tone: Tone;
}

export interface VehicleSummary {
  id: string;
  name: string;
  year?: string;
  kind: VehicleKind;
  category: string;
  transmission: string;
  fuel: string;
  seats: number;
  /** Client price per day, commission included. */
  pricePerDay: number;
  /** Lower daily price from `tierFromDays` days (bikes). */
  tierPricePerDay?: number;
  /** Days from which `tierPricePerDay` applies (2 when not given). */
  tierFromDays?: number;
  /**
   * Every price step of a bike (the server's `pricingTiers`), by ascending `fromDays` (PR-07). A stay of n billable
   * days costs the step with the highest `fromDays` ≤ n, else `pricePerDay` (`lib/pricing` `dailyPrice`).
   */
  tiers?: VehiclePriceTier[];
  deposit: number;
  freeDelivery: boolean;
  /** Cut-out vehicle image (transparent PNG). */
  image: string;
  tag?: VehicleTag;
  city: string;
}

/** A bike price step: `pricePerDay` (client price, commission included) from `fromDays` billable days. */
export interface VehiclePriceTier {
  fromDays: number;
  pricePerDay: number;
}

export interface AgencySummary {
  id: string;
  name: string;
  city: string;
  verified: boolean;
  responseTime: string;
  rating?: { average: number; count: number };
}

/** Public agency page (`/pages/agency/:agencyName`). */
export interface Agency extends AgencySummary {
  slug: string;
  description: string;
  address: string;
  memberSince: string;
  vehicles: VehicleSummary[];
}

export interface BlogPostSummary {
  id: string;
  title: string;
  excerpt: string;
  image: string;
  imageAlt: string;
  date: string;
  category: string;
  readingMinutes: number;
}

export interface BlogPost extends BlogPostSummary {
  /** Body as paragraphs of plain text (the http mapper sanitises the CMS HTML into this). */
  paragraphs: string[];
}

export interface IncludedItem {
  icon: 'shieldCheck' | 'road' | 'sparkle' | 'headset' | 'truck' | 'clock';
  title: string;
  text: string;
}

export interface Vehicle extends VehicleSummary {
  /** Photos shown beside the main cut-out. */
  gallery: string[];
  agency: AgencySummary;
  mileage: string;
  minDays: number;
  rating?: { average: number; count: number };
  included: IncludedItem[];
  /** Agency rules as plain text paragraphs. */
  rules: string[];
  /** Days already booked (ISO dates). */
  unavailable: string[];
}

/**
 * One row of a price breakdown. Money stays numeric so the UI formats it in the chosen currency:
 * - with `unitPrice`, the UI shows «<unitPrice> × <quantityLabel>» (e.g. «299 MAD × 4 jours»)
 * - with `valueText`, the value column shows that text instead of an amount (e.g. «Incluse»)
 * - `discount` amounts are positive and shown with a minus sign
 */
export interface PriceLine {
  kind: 'base' | 'discount' | 'fee' | 'info';
  label: string;
  amount: number;
  unitPrice?: number;
  quantityLabel?: string;
  valueText?: string;
}

/**
 * The amount of record comes from the server quote. Guests cannot ask the server
 * (`POST /api/reservations/quote` needs a session): they get a front-end estimate computed with the
 * same rules (`lib/pricing`), flagged `isEstimate` so the page says «Estimation».
 */
export interface Quote {
  days: number;
  /** Return later than the pickup time + tolerance adds one billable day. */
  extraDay: boolean;
  pricePerDay: number;
  lines: PriceLine[];
  total: number;
  paymentMode: PaymentMode;
  /** Charged online (pre-authorised today). */
  payOnline: number;
  /** Paid to the agency at pickup (commission_only). */
  payAtPickup: number;
  deposit: number;
  longStay?: { pct: number; amount: number };
  /** True when computed in the browser (guest), false for the server quote. */
  isEstimate: boolean;
  /** Delivery fee of a delivery quote, in MAD (0 = free); undefined when the source does not price it (PR-09). */
  deliveryFee?: number;
  /**
   * The server quote's `converted` block (PR-11, Decision 45): what the card is charged online in the chosen
   * currency, at Cardnd's rate. Only for a server quote asked in EUR or USD; MAD stays the reference everywhere else.
   */
  converted?: QuoteCharge;
}

/** The online charge of a quote in the presentment currency (major units, 2 decimals). */
export interface QuoteCharge {
  currency: Currency;
  /** Charged online today (the `platformFee` of the block: the commission or the whole total, per mode). */
  payOnline: number;
  /** The whole booking total in that currency. */
  total: number;
  /** Effective rate from MAD (Cardnd's margin included). */
  fxRate: number;
}

export interface RefundTier {
  when: string;
  outcome: string;
  tone: Tone;
}

export interface AvailabilityResult {
  available: boolean;
  /** First conflicting day, when not available and the source knows it (the backend does not). */
  conflict?: string;
  /** Platform blackout on these dates: nothing can be booked (check-availability `blackout: true`). */
  blackout?: boolean;
}

/* ── Experiences ── */

export interface ExperienceSummary {
  id: string;
  title: string;
  category: string;
  city: string;
  image: string;
  imageAlt: string;
  duration: string;
  durationMinutes: number;
  groupLabel: string;
  difficulty?: string;
  /** The server's «dès» price (MAD); null when nothing is priced (no price is shown, never 0). */
  fromPrice: number | null;
  /** Strike-through compare-at price, when the supplier has one. */
  compareAt?: number;
  priceUnit: 'person' | 'vehicle';
  /** Canonical public path `/activities/:city/:slug/:publicId` (P6/P10), when the activity has a public id. */
  path?: string;
  /** Clients' post-trip ratings (`ratingStats`), when there is at least one (API-ACT-1). */
  rating?: { average: number; count: number };
}

export interface ExperiencePack {
  id: string;
  label: string;
  /**
   * The option's own price (MAD) from the server's `optionsSummary` (AC-02): per person, or the flat group
   * total when `basis` is `per_group`; null when the server gives none (shown without a price, never 0).
   */
  price: number | null;
  /** `per_group`: one flat price for the whole group (privatisation). Per person when absent. */
  basis?: 'per_person' | 'per_group';
  /** People a flat group price covers, when the option says. */
  groupSize?: number;
  popular?: boolean;
  /** Price per person from 3 participants. */
  groupPrice?: number;
  /* ── P6: what the option itself asks for (AC-05, pickup, eligibility, questions, requirements) ── */
  description?: string;
  /** Participants (adults + children) the option takes; `fixed` when min === max. */
  bounds?: ParticipantBounds;
  /** Infants (under 2, free, no seat) can come along. */
  infants?: boolean;
  /** Hotel or riad pickup offered by this option. */
  pickup?: ExperiencePickup;
  /** Free entry on request (the provider checks a proof): its instructions. */
  eligibility?: { instructions: string };
  /** Questions the provider asks at booking. */
  questions?: ExperienceQuestion[];
  /** Per-participant details the provider needs (ages, heights, weights). */
  requirements?: ExperienceRequirements;
  /** Self-drive activities (quad, buggy): drivers and passengers with their ages. */
  vehicleAllocation?: ExperienceVehicleAllocation;
  /** Add-ons offered with this option (ids of `Experience.addons`); all when absent. */
  addonIds?: string[];
}

/** Participant bounds of an option (legacy `participantBounds`, AC-05). */
export interface ParticipantBounds {
  min: number;
  max: number;
  fixed: boolean;
}

export interface ExperiencePickupPlace {
  id: string;
  name: string;
  /** Extra charge for this place, MAD (0 when none). */
  surcharge: number;
  instructions?: string;
}

/** Pickup at the client's hotel or riad: a list of places, a zone (needs a location) or an address the provider confirms. */
export interface ExperiencePickup {
  coverage: 'place_list' | 'radius' | 'area' | 'on_confirmation';
  places: ExperiencePickupPlace[];
  instructions?: string;
  /** When the client learns the exact pickup time («la veille avant 20 h»). */
  confirmationTiming?: string;
}

/** The client's pickup choice: a listed place, or an address (with its location when the zone needs one). */
export interface ExperiencePickupChoice {
  placeId?: string;
  address: string;
  lat?: number;
  lng?: number;
}

export interface ExperienceQuestion {
  key: string;
  label: string;
  type: 'text' | 'select' | 'time';
  required: boolean;
  choices: string[];
}

export interface ExperienceRequirements {
  minAge?: number;
  minHeightCm?: number;
  /** Above this weight the provider confirms the booking himself. */
  approvalAboveWeightKg?: number;
  /** At least one participant aged 18 or more. */
  adultCompanion: boolean;
}

export interface ExperienceVehicleAllocation {
  peoplePerVehicle: number;
  maxVehicles?: number;
  driverMinAge?: number;
  passengerMinAge?: number;
  instructions?: string;
}

export interface ExperienceAddon {
  id: string;
  label: string;
  price: number;
}

/** One open departure (`GET /api/client/activities/:id/slots`). */
export interface ExperienceSlot {
  id: string;
  /** Calendar day `yyyy-MM-dd` (the backend reads the slot date in UTC). */
  date: string;
  /** `HH:mm`. */
  time: string;
  endTime?: string;
  /** The option it belongs to; null = any option. */
  packId: string | null;
  /** Seats still sellable, null when uncapped. */
  seatsLeft: number | null;
}

/** A free-entry request (legacy `ActivityEligibilityRequest`, AC-04). */
export interface EligibilityRequest {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  /** Already used by a booking (an approved request then grants nothing). */
  used: boolean;
  /** The provider's note, when it answered. */
  note?: string;
}

export interface EligibilityRequestInput {
  experienceId: string;
  packId: string;
  date: string;
  participants: { adults: number; children: number; infants: number };
  free: { adults: number; children: number; infants: number };
  /** Reference of the proof sent to the provider (≤ 500 characters). */
  proof: string;
}

/** Everything the experience checkout sends with the selection (API-ACT-7). */
export interface ExperienceBookingInput {
  selection: ExperienceSelection;
  contact: { firstName: string; lastName: string; email: string; phone: string };
  /** Answers to the option's questions, by key. */
  answers: Record<string, string>;
  waiverAccepted: boolean;
  /** Per participant (adults, then children, then infants), when the option needs them. */
  ages?: number[];
  heightsCm?: number[];
  weightsKg?: number[];
  vehicleAllocation?: { driverAges: number[]; passengerAges: number[] };
  /** Currency the card is charged in (the server converts at its own rate). */
  currency: Currency;
}

/** Tickets the provider issued for a booking (entry tickets), resent on request. */
export interface ProviderTickets {
  status: 'pending' | 'issued';
  documents: { title: string; url: string }[];
  confirmedStartTime?: string;
  instructions?: string;
}

export interface Experience extends ExperienceSummary {
  gallery: string[];
  startTime?: string;
  cancellation: string;
  mode: string;
  place: string;
  description: string;
  included: string;
  excluded: string;
  bring: string;
  meetingPoint: string;
  pricing: 'per_person' | 'tiered' | 'per_group';
  /** Tiered pricing: price per person for 1, 2, 3 and 4+ adults. */
  tiers?: number[];
  maxGroup: number;
  childPrice: number | null;
  packs: ExperiencePack[];
  /** The option selected first (the server's reference option `fromPriceOptionId`, else its default, AC-06…AC-10). */
  defaultPackId?: string;
  addons: ExperienceAddon[];
  languages: string[];
  /** Public id of the canonical URL (P6/P10), when the activity has one. */
  publicId?: string;
  /** The supplier's waiver to accept at checkout. */
  waiver?: { required: boolean; text?: string; documentUrl?: string };
}

export interface ExperienceDay {
  date: string;
  available: boolean;
  /** Few places left. */
  low: boolean;
  /** Departure times open that day (`HH:mm`), when the source lists them. */
  times?: string[];
}

export interface ExperienceSelection {
  experienceId: string;
  date: string;
  packId: string;
  adults: number;
  children: number;
  addonIds: string[];
  /* P6 */
  /** Departure `HH:mm` chosen among the day's open slots (none: the first departure). */
  time?: string;
  /** Under 2: free, no seat. */
  infants?: number;
  /** Hotel or riad pickup, when the option offers it. */
  pickup?: ExperiencePickupChoice;
  /** Approved free-entry request (eligibility grant). */
  grantId?: string;
}

export interface ExperienceQuote {
  lines: PriceLine[];
  total: number;
  participants: number;
  paymentMode: PaymentMode;
  /* P6: the server's verdict on the selection */
  /** False when the server would refuse it now (cut-off passed, pickup missing). */
  bookable?: boolean;
  blockedBy?: 'cutoff' | 'pickup_required' | null;
  /** The option needs a pickup place or address. */
  pickupRequired?: boolean;
  /** What the server made of the pickup choice. */
  pickup?: { covered: boolean; needsConfirmation: boolean; placeName?: string; surcharge: number; reason?: string };
  /** Departure the quote priced. */
  time?: string;
}

export interface ExperienceFilters {
  city: string;
  date: string | null;
  category: string;
  duration: '' | 'court' | 'moyen' | 'long' | 'journee';
}

/* ── Account ── */

export interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  memberSince: string;
  /** Profile photo URL, when the client uploaded one. */
  avatar?: string;
  /** `yyyy-MM-dd`, when given. */
  dateOfBirth?: string;
  bio?: string;
  /** Marketing e-mails accepted (P11 consent modal asks while this is not `true`). */
  marketingConsent?: boolean | null;
}

/** Editable profile fields (the e-mail is the account key: not editable here). */
export interface ProfileUpdate {
  name?: string;
  phone?: string;
  /** `yyyy-MM-dd`; an empty string clears it. */
  dateOfBirth?: string;
  bio?: string;
  /** New profile photo (≤ 10 MB). */
  picture?: File;
  /** Removes the current photo. */
  removePicture?: boolean;
}

export type TripStatus = 'pending' | 'confirmed' | 'completed' | 'cancelled';

export interface Trip {
  id: string;
  ref: string;
  kind: 'vehicle' | 'experience';
  status: TripStatus;
  title: string;
  image: string;
  /** `cutout` = vehicle PNG on the soft stage, `photo` = full-bleed photo. */
  imageLook: 'cutout' | 'photo';
  city: string;
  start: string;
  end: string;
  pickupTime: string;
  returnTime: string;
  place: string;
  total: number;
  payOnline: number;
  agency: string;
  pin?: string;
  canRate: boolean;
  /** The vehicle booked (to book it again), for vehicle trips. */
  vehicleId?: string;
  /* Trip actions (P5): what the server would accept for this booking. */
  /** Self-cancel allowed (non-terminal and the start day not reached). */
  canCancel?: boolean;
  /** Extension request allowed (vehicle, pending or confirmed, not started, none asked yet). */
  canExtend?: boolean;
  /** The extension the client asked for, with the agency's answer. */
  extension?: TripExtension;
  /** A voucher PDF can be downloaded (confirmed or completed with a voucher code). */
  voucherPdf?: boolean;
  /** Experiences: the cancellation terms of the booking, in French. */
  cancellationText?: string;
  /**
   * Experiences (TR-09): what the card carries, from `payment.status`: `preauthorised` (`requires_capture`,
   * held, not charged yet) or `paid` (`succeeded`). Absent otherwise (no payment, released, failed).
   */
  paymentStatus?: 'preauthorised' | 'paid';
  /** Experiences: the amount held or charged, in the currency of the card (stamped by the server). */
  charged?: { amount: number; currency: string };
}

export interface TripExtension {
  status: 'pending' | 'accepted' | 'rejected';
  returnDate: string;
  returnTime: string;
  /** New total and the difference, priced by the server. */
  newTotal?: number;
  difference?: number;
}

/** Why the client cancels (backend enum, one of five; the cancel sheet asks for it). */
export type CancelReason = 'cheaper_offer' | 'plans_changed' | 'support_no_contact' | 'car_mismatch' | 'other';

export interface Cart {
  id: string;
  title: string;
  image: string;
  start: string;
  end: string;
  total: number;
  /** The vehicle page to resume the booking. */
  vehicleId: string;
  city?: string;
}

export interface Favourites {
  vehicles: VehicleSummary[];
  experiences: ExperienceSummary[];
}

export interface Thread {
  id: string;
  kind: 'support' | 'agency';
  name: string;
  subtitle: string;
  initials: string;
  unread: boolean;
  time: string;
  messages: Message[];
  /** Suggested replies offered by the conversation itself (support bot choices); else the page's defaults. */
  quickReplies?: string[];
  /* Chat (P8) */
  /** Unread messages for the client (the badge sums them); `unread` is `unreadCount > 0`. */
  unreadCount?: number;
  /**
   * `bot`: the support assistant is asking (its choices are `quickReplies`); `open`: people write;
   * `resolved`: support closed it (rating, then «Nouvelle conversation»); `closed`: agency thread, read-only.
   */
  state?: 'bot' | 'open' | 'resolved' | 'closed';
  /** The pending bot question is the 1–5 satisfaction rating (its choices are «1»…«5»). */
  ratingPrompt?: boolean;
  /** A resolved support thread already carries the client's rating. */
  rated?: boolean;
  /** Older messages exist before the first one loaded (pages of 50). */
  hasMore?: boolean;
  /** Support thread not created yet: the first message creates it (`SUPPORT_DRAFT_ID`). */
  draft?: boolean;
}

/** Id of the support thread before it exists on the server (created by its first message). */
export const SUPPORT_DRAFT_ID = 'support-new';

export interface Message {
  id: string;
  from: 'me' | 'them';
  text: string;
  time: string;
  /** ISO day of the message (day separators). */
  date?: string;
  /** The conversation it was posted in, when the send created it (first message of a support thread). */
  threadId?: string;
}

/** A page of older messages (`before` the oldest loaded one). */
export interface MessagePage {
  messages: Message[];
  hasMore: boolean;
}

export interface ProfileStats {
  trips: number;
  favourites: number;
}

/* ── Bookings ── */

export interface Driver {
  firstName: string;
  lastName: string;
  email: string;
  /** International number: dial code + local digits, e.g. «+33612345678». */
  phone: string;
  licenceYears: 'lt2' | '2' | '5' | '10';
  /** Country of residence as the server stores it (French name); «Maroc» when not given. */
  country?: string;
}

export interface VehicleBookingInput {
  vehicleId: string;
  search: VehicleSearch;
  delivery: { enabled: boolean; address: string };
  driver: Driver;
  /** Code validated with `coupons.check` (the server applies the discount). */
  coupon?: string;
  /** Currency the card is charged in (the server converts at its own rate). Default MAD. */
  currency?: Currency;
}

export interface Booking {
  id: string;
  ref: string;
  kind: 'vehicle' | 'experience';
  status: 'pending' | 'confirmed';
  title: string;
  image: string;
  city: string;
  start: string;
  end: string;
  pickupTime: string;
  returnTime: string;
  place: string;
  days: number;
  total: number;
  payOnline: number;
  deposit: number;
  pin: string;
  agency: string;
  /** Paid at the agency at pickup (commission_only), when known. */
  payAtPickup?: number;
  /** Name on the booking (driver or lead traveller). */
  driverName?: string;
  /** Experiences: chosen pack, participants and end time of the slot. */
  packLabel?: string;
  participants?: { adults: number; children: number; infants?: number };
  endTime?: string;
  /* P6: experience voucher and provider tickets (`GET /api/client/activity-reservations/:id`) */
  /** Signed QR payload the provider scans (else the QR carries the reference). */
  qrToken?: string;
  /** A voucher PDF exists (confirmed or completed with a voucher). */
  voucherPdf?: boolean;
  tickets?: ProviderTickets;
  /** Experience being booked (detail link of the success page). */
  experienceId?: string;
  /** Vehicle booked, for vehicle bookings. */
  vehicleId?: string;
  /**
   * Online payment to complete, right after creation (`POST /api/reservations` and
   * `POST /api/client/activity-reservations`): mount Stripe with `clientSecret`, or nothing to pay
   * when `zeroFee`. Absent on bookings read back later.
   */
  payment?: BookingPayment;
  /** Meta event id sent as `X-Meta-Event-Id` with the reservation (the Purchase pixel reuses it). */
  metaEventId?: string;
}

export interface BookingPayment {
  clientSecret?: string;
  paymentIntentId?: string;
  zeroFee: boolean;
  /** What Stripe will charge, in `currency` (minor units are not used: major units). */
  amount?: number;
  currency?: string;
}

export interface PartnerLead {
  type: 'cars' | 'motos' | 'activities';
  company: string;
  city: string;
  contactName: string;
  email: string;
  phones: string[];
  address: string;
  fleetSize: string;
  /** Neighbourhood or area in the city. */
  zone?: string;
  /** Manager photo (optional upload). */
  photo?: File | null;
}

/* ── Contact form (P14): `POST /api/send-email` ── */

/** A message to the support inbox, as the contact form collects it. */
export interface ContactMessage {
  name: string;
  email: string;
  /** Optional; sent at the top of the message («Téléphone: …»), as the legacy form did. */
  phone?: string;
  /** The chosen subject's label. */
  subject: string;
  message: string;
  /** Prefixes the subject with «[URGENT] ». */
  urgent: boolean;
}

/* ── Payments and coupons (Stripe card step, shared by the vehicle checkout, cart resume and experiences) ── */

/** `GET /api/payments/config`. `provider: 'demo'` is the mock data source: no card form, nothing charged. */
export interface PaymentConfig {
  provider: 'stripe' | 'demo';
  publishableKey: string | null;
  /** False when the server has no Stripe key: online payment is unavailable. */
  configured: boolean;
}

/** `POST /api/payments/finalize`: what the server read from Stripe after the card step. */
export interface PaymentFinalizeResult {
  /** Stripe status (`requires_capture` = card authorised, `succeeded` = charged). */
  status: string;
  /** Authorised or charged (PAY-04). */
  paid: boolean;
  reservationId?: string;
}

/** `GET /api/coupons/check`: the coupon as the server validated it. */
export interface CouponCheck {
  /** Normalised code (upper case), the one to send with the booking. */
  code: string;
  label: string;
  /** Client discount, in percent. */
  pct: number;
}

/* ── Trip actions, reviews, partner status, results paging (P5, P13, P15) ── */

/** `GET /api/reservations/:id/cancel-preview`: what cancelling now would give back (the server's figure). */
export interface CancelPreview {
  cancellable: boolean;
  /** Why not, when not cancellable. */
  blocked?: 'already_terminal' | 'start_date_reached';
  /** Refund in `currency` (the card's currency), 0 when nothing comes back. */
  refundAmount: number;
  currency: string;
  /** Share of the amount paid online, in percent. */
  pct: number;
  /** Paid online, in `currency`. */
  paidOnline: number;
  /** The card was only authorised: cancelling releases the hold, nothing was charged. */
  released: boolean;
  /** Nothing was paid online (cash booking or unpaid). */
  noOnlinePayment: boolean;
  /** End of the free-cancellation window (ISO), when the policy has one. */
  freeUntil?: string;
}

/** The questions asked when the pickup is less than 48 h away (`alertAnswers`). */
export interface CancelAlertAnswers {
  supportReachedOut: boolean | null;
  supportReachedOutNote: string;
  foundCheaperCar: boolean | null;
  foundCheaperCarNote: string;
  plansChanged: boolean | null;
  plansChangedNote: string;
}

export interface CancelRequest {
  reason: CancelReason;
  /** ≤ 500 characters. */
  note?: string;
  alertAnswers?: CancelAlertAnswers;
}

/** What happened to the money once cancelled (`refund` of the cancel response). */
export interface CancelOutcome {
  /** Card hold released: nothing was charged. */
  released: boolean;
  /** Refund executed now. */
  refunded: boolean;
  /** Refund created, settles in a few days. */
  pending: boolean;
  amount: number;
  pct: number;
  currency: string;
  /** Nothing was paid online. */
  noOnlinePayment: boolean;
}

export interface ExtensionRequest {
  /** Local `yyyy-MM-dd`, sent as is (no UTC shift). */
  returnDate: string;
  returnTime: string;
  reason?: string;
}

/** Criteria of a rating: the vehicle set or the experience set, each 1–5. */
export type VehicleRatingCriterion = 'service' | 'carCondition' | 'valueForMoney' | 'carPerformance' | 'cleanliness';
export type ExperienceRatingCriterion = 'knowledge' | 'fun' | 'value' | 'communication' | 'accuracy';

export interface TripRatingInput {
  scores: Partial<Record<VehicleRatingCriterion | ExperienceRatingCriterion, number>>;
  /** Required by the server. */
  comment: string;
}

/** A review the client wrote («Mes avis», `GET /api/ratings/client`). */
export interface Review {
  id: string;
  vehicleId?: string;
  title: string;
  image: string;
  /** «Atlas Cars · Marrakech», when known. */
  agencyLine: string;
  /** ISO date-time. */
  createdAt: string;
  average: number;
  scores: Partial<Record<VehicleRatingCriterion, number>>;
  comment: string;
}

/** `GET /api/client/auth/partner/status`. */
export interface PartnerStatus {
  status: 'none' | 'requested' | 'approved' | 'rejected';
  /** The agency dashboard (external), for approved partners. */
  dashboardUrl: string | null;
}

/** One page of vehicle results (`page` from 1). */
export interface VehicleResultsPage {
  items: VehicleSummary[];
  page: number;
  totalPages: number;
  /** Platform blackout on these dates (search `blackout: true`): nothing can be booked, `items` is empty (API-VEH-5). */
  blackout?: boolean;
}

/** A downloadable file (voucher PDF). */
export interface DownloadFile {
  blob: Blob;
  fileName: string;
}
